import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AlertData, DecodingAgentError } from '../../utils/getDecodingAgent';
import { callDecodingAgent } from '../../utils/getDecodingAgent';

// Define the state interface
export interface DecodingAgentState {
  result: string | null;
  isLoading: boolean;
  error: string | null;
}

// Initial state
const initialState: DecodingAgentState = {
  result: null,
  isLoading: false,
  error: null,
};

// Create async thunk for calling the decoding agent
export const fetchDecodingAgentAnalysis = createAsyncThunk(
  'decodingAgent/fetchAnalysis',
  async (alertData: AlertData, { rejectWithValue }) => {
    try {
      return await callDecodingAgent(alertData);
    } catch (err) {
      const error = err as DecodingAgentError;
      console.error('Decoding agent error:', {
        message: error.message,
        status: error.status
      });
      return rejectWithValue(error.message);
    }
  }
);

// Create the slice
const decodingAgentSlice = createSlice({
  name: 'decodingAgent',
  initialState,
  reducers: {
    clearResults: (state) => {
      state.result = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDecodingAgentAnalysis.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDecodingAgentAnalysis.fulfilled, (state, action: PayloadAction<string>) => {
        state.isLoading = false;
        state.result = action.payload;
        state.error = null;
      })
      .addCase(fetchDecodingAgentAnalysis.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'An error occurred';
      });
  },
});

// Export actions and reducer
export const { clearResults } = decodingAgentSlice.actions;
export default decodingAgentSlice.reducer;